import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { ThemeProvider as StyledComponentsThemeProvider } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import GlobalStyle from "./styles/GlobalStyle";
import { theme } from "./styles/theme";
import LoginPage from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import DashboardLayout from "./components/layout/DashboardLayout";
import Notification from "./components/shared/Notification";
import ProtectedRoute from "./components/shared/ProtectedRoute";
import Home from "./pages/Home";
import { setLoading, setUser } from "./store/slices/authSlice";
import { SearchProvider } from "./context/SearchContext";
import InactivityTimer from "./components/InactivityTimer";
import InboundStudies from "./pages/InboundStudies";
import { NotificationProvider } from "./context/NotificationContext";
import Reports from "./pages/Reports";
import { menuConfig } from "./components/layout/sidebarConfig";
import UserManagement from "./pages/UserManagement";
import ClientManagement from "./pages/ClientManagement";

const App = () => {
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const isLoggedIn = useSelector((state) => state.auth.loggedIn);
  const userAccessLevel = useSelector((state) => state.auth.access_level);
  const effectRan = useRef(false);

  useEffect(() => {
    if (effectRan.current) return;
    const token = localStorage.getItem("token");
    if (token) {
      dispatch(setUser());
    } else {
      dispatch(setLoading(false));
    }
    effectRan.current = true;
  }, [dispatch]);

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const handleClose = () => {
    setNotification({ ...notification, open: false });
  };

  if (auth.loading) {
    return <div>Loading...</div>;
  }

  return (
    <MuiThemeProvider theme={theme}>
      <StyledComponentsThemeProvider theme={theme}>
        <GlobalStyle />
        <SearchProvider>
          <NotificationProvider>
            <Router>
              {isLoggedIn && <InactivityTimer />}
              <Routes>
                <Route
                  path="/login"
                  element={<LoginPage showNotification={showNotification} />}
                />
                <Route
                  path="/forgot-password"
                  element={
                    !auth.loading ? (
                      <ForgotPassword showNotification={showNotification} />
                    ) : (
                      <div>Loading...</div>
                    )
                  }
                />
                <Route element={<ProtectedRoute />}>
                  <Route
                    path="/dashboard/*"
                    element={
                      <DashboardLayout>
                        <Routes>
                          <Route path="/" element={<Home />} />
                          {menuConfig.some(
                            (menu) =>
                              menu.path === "/dashboard/inbound-studies" &&
                              menu.access.includes(userAccessLevel)
                          ) && (
                            <Route
                              path="inbound-studies"
                              element={<InboundStudies />}
                            />
                          )}

                          {menuConfig.some(
                            (menu) =>
                              menu.path === "/dashboard/reports" &&
                              menu.access.includes(userAccessLevel)
                          ) && <Route path="reports" element={<Reports />} />}
                          {menuConfig.some(
                            (menu) =>
                              menu.path === "/dashboard/user-management" &&
                              menu.access.includes(userAccessLevel)
                          ) && (
                            <Route
                              path="user-management"
                              element={<UserManagement />}
                            />
                          )}
                          {menuConfig.some(
                            (menu) =>
                              menu.path === "/dashboard/client-management" &&
                              menu.access.includes(userAccessLevel)
                          ) && (
                            <Route
                              path="client-management"
                              element={<ClientManagement />}
                            />
                          )}

                          <Route path="*" element={<Navigate to="/" />} />
                        </Routes>
                      </DashboardLayout>
                    }
                  />
                </Route>
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
              <Notification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={handleClose}
              />
            </Router>
          </NotificationProvider>
        </SearchProvider>
      </StyledComponentsThemeProvider>
    </MuiThemeProvider>
  );
};

export default App;
